/**
 * LiveRecordingPanel Component
 *
 * Provides UI for recording audio and displaying live transcription.
 * Used in the Interview Setup flow as an alternative to pasting text.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Mic,
  MicOff,
  Pause,
  Play,
  Square,
  AlertCircle,
  Loader2,
  CheckCircle,
  Volume2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAudioRecording } from '@/hooks/useAudioRecording'
import { useTranscriptionStream } from '@/hooks/useTranscriptionStream'
import type { RecordingState } from '@ispot/shared'

interface LiveRecordingPanelProps {
  /** Called when recording is complete with the transcript */
  onComplete: (transcript: string) => void
  /** Called when user cancels recording */
  onCancel: () => void
  /** Minimum transcript length required */
  minLength?: number
}

/**
 * Format duration as MM:SS
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Get state display info
 */
function getStateInfo(state: RecordingState): { label: string; color: string } {
  switch (state) {
    case 'idle':
      return { label: 'Ready to record', color: 'text-text-muted' }
    case 'recording':
      return { label: 'Recording', color: 'text-error' }
    case 'paused':
      return { label: 'Paused', color: 'text-warning' }
    case 'processing':
      return { label: 'Processing', color: 'text-business' }
    case 'complete':
      return { label: 'Complete', color: 'text-market' }
    case 'error':
      return { label: 'Error', color: 'text-error' }
    default:
      return { label: 'Unknown', color: 'text-text-muted' }
  }
}

export function LiveRecordingPanel({
  onComplete,
  onCancel,
  minLength = 50,
}: LiveRecordingPanelProps) {
  // State
  const [whisperAvailable, setWhisperAvailable] = useState<boolean | null>(null)
  const [checkingHealth, setCheckingHealth] = useState(true)

  // Transcript display
  const transcriptRef = useRef<HTMLDivElement>(null)

  // Audio recording hook
  const {
    state: recordingState,
    duration,
    error: recordingError,
    start: startRecording,
    stop: stopRecording,
    pause: pauseRecording,
    resume: resumeRecording,
    isSupported,
  } = useAudioRecording({
    onChunkReady: handleChunkReady,
    chunkIntervalMs: 5000, // 5 second chunks
  })

  // Transcription stream hook
  const {
    sendChunk,
    checkHealth,
    isProcessing,
    transcribedText,
    error: transcriptionError,
    clearTranscript,
  } = useTranscriptionStream({
    onTranscript: handleTranscript,
    onError: handleTranscriptionError,
  })

  /**
   * Handle audio chunk ready - send for transcription
   */
  function handleChunkReady(chunk: Blob, chunkIndex: number) {
    sendChunk(chunk, chunkIndex)
  }

  /**
   * Handle transcript received
   */
  function handleTranscript(text: string, isFinal: boolean) {
    // Auto-scroll transcript
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight
    }
  }

  /**
   * Handle transcription error
   */
  function handleTranscriptionError(error: Error) {
    // Error is displayed via the hook's error state
  }

  /**
   * Check whisper server health on mount
   */
  useEffect(() => {
    async function check() {
      setCheckingHealth(true)
      const health = await checkHealth()
      setWhisperAvailable(health.available)
      setCheckingHealth(false)
    }
    check()
  }, [checkHealth])

  /**
   * Handle start recording
   */
  const handleStart = useCallback(async () => {
    clearTranscript()
    await startRecording()
  }, [clearTranscript, startRecording])

  /**
   * Handle stop recording
   */
  const handleStop = useCallback(async () => {
    await stopRecording()
  }, [stopRecording])

  /**
   * Handle use transcript
   */
  const handleUseTranscript = useCallback(() => {
    if (transcribedText.trim().length >= minLength) {
      onComplete(transcribedText.trim())
    }
  }, [transcribedText, minLength, onComplete])

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(async () => {
    if (recordingState === 'recording' || recordingState === 'paused') {
      await stopRecording()
    }
    clearTranscript()
    onCancel()
  }, [recordingState, stopRecording, clearTranscript, onCancel])

  // Derived state
  const stateInfo = getStateInfo(recordingState)
  const error = recordingError || transcriptionError
  const canStart = recordingState === 'idle' && whisperAvailable
  const canStop = recordingState === 'recording' || recordingState === 'paused'
  const canPause = recordingState === 'recording'
  const canResume = recordingState === 'paused'
  const canUseTranscript = (recordingState === 'complete' || recordingState === 'idle') &&
    transcribedText.trim().length >= minLength
  const isTranscriptValid = transcribedText.trim().length >= minLength

  // Browser doesn't support recording
  if (!isSupported) {
    return (
      <Card className="p-6 bg-warm-50">
        <div className="flex items-center gap-3 text-error">
          <AlertCircle className="w-5 h-5" />
          <div>
            <p className="font-medium">Audio recording not supported</p>
            <p className="text-sm text-text-muted">
              Your browser doesn't support audio recording. Please use Chrome, Firefox, or Edge.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onCancel} className="mt-4">
          Back to Paste Mode
        </Button>
      </Card>
    )
  }

  // Checking whisper health
  if (checkingHealth) {
    return (
      <Card className="p-6 bg-warm-50">
        <div className="flex items-center gap-3 text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Checking transcription service...</span>
        </div>
      </Card>
    )
  }

  // Whisper not available
  if (!whisperAvailable) {
    return (
      <Card className="p-6 bg-warm-50">
        <div className="flex items-center gap-3 text-warning">
          <AlertCircle className="w-5 h-5" />
          <div>
            <p className="font-medium">Transcription service unavailable</p>
            <p className="text-sm text-text-muted">
              The local Whisper server is not running. Please start it to enable recording.
            </p>
            <code className="block mt-2 p-2 bg-warm-100 rounded text-xs font-mono">
              ./whisper-server --model models/ggml-base.en.bin --port 8080
            </code>
          </div>
        </div>
        <Button variant="outline" onClick={onCancel} className="mt-4">
          Back to Paste Mode
        </Button>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Recording Controls */}
      <Card className="p-4 bg-warm-50">
        <div className="flex items-center justify-between">
          {/* Control buttons */}
          <div className="flex items-center gap-2">
            {/* Start/Stop button */}
            {recordingState === 'idle' || recordingState === 'complete' ? (
              <Button
                onClick={handleStart}
                disabled={!canStart}
                className="gap-2 bg-error hover:bg-error/90 text-white"
              >
                <Mic className="w-4 h-4" />
                Start Recording
              </Button>
            ) : (
              <Button
                onClick={handleStop}
                disabled={!canStop}
                variant="destructive"
                className="gap-2"
              >
                <Square className="w-4 h-4" />
                Stop
              </Button>
            )}

            {/* Pause/Resume button */}
            {canPause && (
              <Button onClick={pauseRecording} variant="outline" size="icon">
                <Pause className="w-4 h-4" />
              </Button>
            )}
            {canResume && (
              <Button onClick={resumeRecording} variant="outline" size="icon">
                <Play className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Status and duration */}
          <div className="flex items-center gap-4">
            {/* Recording indicator */}
            {recordingState === 'recording' && (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-error animate-pulse" />
                <Volume2 className="w-4 h-4 text-error animate-pulse" />
              </div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
              <div className="flex items-center gap-2 text-business">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Transcribing...</span>
              </div>
            )}

            {/* Duration */}
            <div className="flex items-center gap-2">
              <span className="text-2xl font-mono font-semibold tabular-nums">
                {formatDuration(duration)}
              </span>
            </div>

            {/* State badge */}
            <Badge
              variant="outline"
              className={cn('text-xs', stateInfo.color)}
            >
              {stateInfo.label}
            </Badge>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mt-3 flex items-center gap-2 text-error text-sm">
            <AlertCircle className="w-4 h-4" />
            {error.message}
          </div>
        )}
      </Card>

      {/* Live Transcript */}
      <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="px-4 py-2 border-b border-warm-200 flex items-center justify-between">
          <span className="text-sm font-medium">Live Transcript</span>
          <span className="text-xs text-text-muted">
            {transcribedText.length} characters
            {transcribedText.length < minLength && transcribedText.length > 0 && (
              <span className="text-warning ml-2">(minimum {minLength})</span>
            )}
          </span>
        </div>

        <div
          ref={transcriptRef}
          className="flex-1 p-4 overflow-auto bg-background"
        >
          {transcribedText ? (
            <p className="text-sm whitespace-pre-wrap">{transcribedText}</p>
          ) : (
            <p className="text-sm text-text-muted italic">
              {recordingState === 'idle'
                ? 'Click "Start Recording" to begin. Your speech will be transcribed in real-time.'
                : recordingState === 'recording'
                  ? 'Listening... speak clearly into your microphone.'
                  : 'Transcript will appear here...'}
            </p>
          )}
        </div>
      </Card>

      {/* Footer actions */}
      <div className="flex justify-between items-center pt-2 border-t border-warm-200">
        <Button variant="ghost" onClick={handleCancel}>
          Cancel Recording
        </Button>

        <div className="flex items-center gap-3">
          {isTranscriptValid && (
            <div className="flex items-center gap-1.5 text-market text-sm">
              <CheckCircle className="w-4 h-4" />
              Ready to use
            </div>
          )}

          <Button
            onClick={handleUseTranscript}
            disabled={!canUseTranscript}
            className="gap-2"
          >
            Use Transcript
          </Button>
        </div>
      </div>
    </div>
  )
}
